import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/client';
import WorkReportPopup from './WorkReportPopup';
import RangeToolbar from './RangeToolbar';
import { toMiamiDateString } from '../utils/dateUtils';
import './WorkReportTable.css';

const DAY_SHORT = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu',
                    Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };

function fmtDate(d) {
    if (!d) return '';
    const dt = new Date(d + 'T12:00:00');
    return dt.toLocaleDateString('en-US', { month:'short', day:'numeric' });
}
function fmtWeek(start, end) {
    const s = new Date(start + 'T12:00:00');
    const e = new Date(end   + 'T12:00:00');
    const opts = { month: 'short', day: 'numeric' };
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year:'numeric' })}`;
}
function fmtTime(t) { return t ? t.slice(0,5) : '—'; }
function fmtH(h)    { return h != null && h > 0 ? `${h}h` : '—'; }
function fmtPay(h, rate) {
    const hrs = Number(h); const r = Number(rate);
    if (!hrs || !r || hrs <= 0) return null;
    return (hrs * r).toFixed(2);
}
// records of one employee/week → [{ rate, days, from }] in date order; `from` = first day at that
// rate when the week has more than one rate (rate history: a raise mid-week shows "3d @ $15/h · 2d @ $16/h from Sep 3")
function rateBreakdown(records) {
    const out = [];
    for (const r of [...records].sort((a, b) => a.workday.localeCompare(b.workday))) {
        if (r.rate == null) continue;
        const rate = Number(r.rate).toFixed(2).replace(/\.00$/, '');
        const last = out[out.length - 1];
        if (last && last.rate === rate) last.days++;
        else out.push({ rate, days: 1, from: r.workday });
    }
    if (out.length === 1) out[0].from = null;
    return out;
}
async function copyText(text) {
    try { await navigator.clipboard.writeText(text); }
    catch { const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch {} document.body.removeChild(ta); }
}

const WorkReportTable = ({ updateKey, externalAdd, onExternalAddHandled, search = '', employeeId = '' }) => {
    const [records,     setRecords]     = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [range,       setRange]       = useState(null);   // { from, to } from RangeToolbar
    const [collapsedW,  setCollapsedW]  = useState(new Set());
    const [collapsedE,  setCollapsedE]  = useState(new Set());
    const [popup,       setPopup]       = useState({ open: false, editData: null, date: null });
    const [deleting,    setDeleting]    = useState(null);
    // inline rate change from the rate pill: PUT /api/work-reports/rate/:employeeID (rate history)
    const [rateEdit,    setRateEdit]    = useState(null);   // { employeeID, name, rate, from, saving, error }

    const saveRate = async () => {
        const rate = Number(rateEdit.rate);
        if (!Number.isFinite(rate) || rate < 0) return setRateEdit(r => ({ ...r, error: 'Enter a rate ≥ 0' }));
        setRateEdit(r => ({ ...r, saving: true, error: null }));
        try {
            await api.put(`/api/work-reports/rate/${rateEdit.employeeID}`, { rate, effective_from: rateEdit.from });
            setRateEdit(null);
            load();
        } catch (e) {
            setRateEdit(r => ({ ...r, saving: false, error: e.response?.data?.error || 'Failed to save' }));
        }
    };

    // week marks: `${employeeID}-${yearweek}` -> { amount?, at, by_name }
    const [paid, setPaid] = useState(new Map());
    const [verified, setVerified] = useState(new Map());

    const load = useCallback(async () => {
        if (!range) return;
        setLoading(true);
        try {
            const marks = mark => api.get(`/api/work-reports/${mark}`, { params: range }).catch(() => ({ data: [] }));
            const [r, p, v] = await Promise.all([api.get('/api/work-reports', { params: range }), marks('paid'), marks('verified')]);
            setRecords(r.data || []);
            const toMap = res => new Map((res.data || []).map(x => [`${x.employeeID}-${x.yearweek}`, x]));
            setPaid(toMap(p)); setVerified(toMap(v));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [range]);

    // Week marks per employee: "paid" stores the amount at that moment, "verified" = hours checked with the employee.
    // Unchecking removes the mark.
    const toggleMark = async (mark, employeeID, yearweek, isOn, amount) => {
        const key = `${employeeID}-${yearweek}`, set = mark === 'paid' ? setPaid : setVerified;
        try {
            await api.put(`/api/work-reports/${mark}`, { employeeID, yearweek, on: !isOn, amount });
            set(m => { const n = new Map(m); if (isOn) n.delete(key); else n.set(key, { employeeID, yearweek, amount, at: new Date().toISOString() }); return n; });
        } catch (e) { alert(e.response?.data?.error || 'Failed to save'); }
    };
    const markTitle = (rec, word) => `${word} ${rec.at ? fmtDate(String(rec.at).slice(0, 10)) : ''}${rec.by_name ? ' by ' + rec.by_name : ''}`;

    useEffect(() => { load(); }, [load, updateKey]);

    // Open popup when parent's + Add button is clicked
    useEffect(() => {
        if (!externalAdd) return;
        setPopup({ open: true, editData: null, date: null });
        onExternalAddHandled?.();
    }, [externalAdd, onExternalAddHandled]);

    // Group: week → employee → records[]
    const grouped = useMemo(() => {
        const weeks = {};
        // page-level search / employee filter (WorkPerformancePage)
        records.filter(r =>
            (!employeeId || String(r.employeeID) === String(employeeId)) &&
            (!search || String(r.employeeName || '').toLowerCase().includes(search))
        ).forEach(r => {
            const wk = r.yearweek;
            if (!weeks[wk]) weeks[wk] = { yearweek: wk, weekStart: r.weekStart, weekEnd: r.weekEnd, employees: {} };
            const empKey = r.employeeID;
            if (!weeks[wk].employees[empKey])
                weeks[wk].employees[empKey] = { employeeID: r.employeeID, employeeName: r.employeeName, records: [] };
            weeks[wk].employees[empKey].records.push(r);
        });
        return Object.values(weeks).sort((a,b) => b.yearweek - a.yearweek);
    }, [records, search, employeeId]);

    const toggleWeek  = (wk) => setCollapsedW(s => { const n = new Set(s); n.has(wk) ? n.delete(wk) : n.add(wk); return n; });
    const toggleEmpl  = (k)  => setCollapsedE(s => { const n = new Set(s); n.has(k)  ? n.delete(k)  : n.add(k);  return n; });

    const handleDelete = async (rec) => {
        if (!window.confirm(`Delete record for ${rec.employeeName} on ${rec.workday}?`)) return;
        setDeleting(`${rec.employeeID}-${rec.workday}-${rec.shift}`);
        try {
            await api.delete(`/api/work-reports/${rec.employeeID}/${rec.workday}/${rec.shift}`);
            load();
        } catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
        finally { setDeleting(null); }
    };

    return (
        <div className="wrt-root">
            {/* Toolbar */}
            <RangeToolbar presets={[4, 8, 12, 26]} initialWeeks={8} onChange={setRange}>
                {loading && <i className="fas fa-spinner fa-spin wrt-spin" />}
            </RangeToolbar>

            {grouped.length === 0 && !loading && (
                <div className="wrt-empty">
                    <i className="fas fa-calendar-times" />
                    <span>No records found for the selected period</span>
                </div>
            )}

            {grouped.map(week => {
                const wCollapsed = collapsedW.has(week.yearweek);
                const empList    = Object.values(week.employees);
                // Week totals
                const weekHours = empList.reduce((s, e) => s + e.records.reduce((ss, r) => ss + (Number(r.hoursWorked) || 0), 0), 0);
                // week payroll: every record at the rate in force that day (rate history); paid employees move to "Paid"
                const payOf = e => e.records.reduce((ss, r) => ss + (Number(fmtPay(r.hoursWorked, r.rate)) || 0), 0);
                const weekPayAll = empList.reduce((s, e) => s + (paid.has(`${e.employeeID}-${week.yearweek}`) ? 0 : payOf(e)), 0);
                const weekPaid = empList.reduce((s, e) => { const p = paid.get(`${e.employeeID}-${week.yearweek}`); return s + (p ? (Number(p.amount) || payOf(e)) : 0); }, 0);

                return (
                    <div key={week.yearweek} className="wrt-week">
                        {/* Week header */}
                        <div className="wrt-week__header" onClick={() => toggleWeek(week.yearweek)}>
                            <i className={`fas fa-chevron-${wCollapsed ? 'right' : 'down'} wrt-chevron`} />
                            <i className="fas fa-calendar-week wrt-cal-icon" />
                            <span className="wrt-week__label">{fmtWeek(week.weekStart, week.weekEnd)}</span>
                            <span className="wrt-week__stats">
                                <span className="wrt-badge wrt-badge--blue">{empList.length} employees</span>
                                <span className="wrt-badge wrt-badge--green">
                                    <i className="fas fa-clock" /> {weekHours.toFixed(1)}h total
                                </span>
                                {weekPayAll > 0 && (
                                    <span className="wrt-badge wrt-badge--pay" onClick={e => e.stopPropagation()}>
                                        To Pay: ${weekPayAll.toFixed(2)}
                                        <button type="button" className="wrt-copy" title="Copy amount" onClick={() => copyText(weekPayAll.toFixed(2))}><i className="far fa-copy" /></button>
                                    </span>
                                )}
                                {weekPaid > 0 && <span className="wrt-badge wrt-badge--muted" title="Marked as paid">Paid: ${weekPaid.toFixed(2)}</span>}
                            </span>
                        </div>

                        {!wCollapsed && empList.map(emp => {
                            const eKey      = `${week.yearweek}-${emp.employeeID}`;
                            const eCollapsed = collapsedE.has(eKey);
                            const empHours  = emp.records.reduce((s, r) => s + (Number(r.hoursWorked) || 0), 0);
                            // pay per day at the rate in force that day (rate history), summed for the week
                            const weekPayNum = emp.records.reduce((s, r) => s + (Number(fmtPay(r.hoursWorked, r.rate)) || 0), 0);
                            const weekPay   = weekPayNum > 0 ? weekPayNum.toFixed(2) : null;
                            const rateGroups = rateBreakdown(emp.records);
                            const paidRec   = paid.get(`${emp.employeeID}-${week.yearweek}`);
                            const verRec    = verified.get(`${emp.employeeID}-${week.yearweek}`);

                            return (
                                <div key={eKey} className="wrt-employee">
                                    {/* Employee sub-header */}
                                    <div className="wrt-empl__header" onClick={() => toggleEmpl(eKey)}>
                                        <i className={`fas fa-chevron-${eCollapsed ? 'right' : 'down'} wrt-chevron wrt-chevron--sm`} />
                                        <span className="wrt-empl__avatar">
                                            {emp.employeeName.charAt(0).toUpperCase()}
                                        </span>
                                        <span className="wrt-empl__name">{emp.employeeName}</span>
                                        <span className="wrt-empl__stats wrt-empl__stats--lg" onClick={e => e.stopPropagation()}>
                                            <span className="wrt-pill">{emp.records.length}d</span>
                                            <span className="wrt-pill wrt-pill--green">{empHours.toFixed(1)}h</span>
                                            {rateGroups.map((g, i) => (
                                                <button key={i} type="button" className="wrt-pill wrt-pill--rate" title={`${g.from ? `Rate $${g.rate}/h from ${fmtDate(g.from)}` : `Rate $${g.rate}/h`} — click to change`}
                                                    onClick={() => setRateEdit({ employeeID: emp.employeeID, name: emp.employeeName,
                                                        rate: String(emp.records[0].currentRate ?? g.rate), from: toMiamiDateString(new Date()), saving: false, error: null })}>
                                                    {rateGroups.length > 1 ? `${g.days}d @ ` : ''}${g.rate}/h{g.from && rateGroups.length > 1 ? ` from ${fmtDate(g.from)}` : ''} <i className="fas fa-pen wrt-pill__pen" />
                                                </button>
                                            ))}
                                            {weekPay && !paidRec && (
                                                <span className="wrt-pill wrt-pill--pay">
                                                    <span className="wrt-pill__label">To Pay:</span> ${weekPay}
                                                    <button type="button" className="wrt-copy" title="Copy amount"
                                                        onClick={() => copyText(weekPay)}><i className="far fa-copy" /></button>
                                                </span>
                                            )}
                                            {paidRec && (
                                                <span className="wrt-pill wrt-pill--pay wrt-pill--muted" title={markTitle(paidRec, 'Paid')}>
                                                    <i className="fas fa-check" /> Paid ${Number(paidRec.amount || weekPay || 0).toFixed(2)}
                                                </span>
                                            )}
                                            <label className="wrt-paid" title={verRec ? markTitle(verRec, 'Verified') + ' — uncheck to undo' : 'Hours checked and confirmed with the employee'}>
                                                <input type="checkbox" checked={!!verRec}
                                                    onChange={() => toggleMark('verified', emp.employeeID, week.yearweek, !!verRec)} /> Verified
                                            </label>
                                            {weekPay && (
                                                <label className="wrt-paid" title={paidRec ? 'Uncheck to reopen this week' : 'Mark this week as paid'}>
                                                    <input type="checkbox" checked={!!paidRec}
                                                        onChange={() => toggleMark('paid', emp.employeeID, week.yearweek, !!paidRec, Number(weekPay))} /> Paid
                                                </label>
                                            )}
                                        </span>
                                    </div>

                                    {/* Records table */}
                                    {!eCollapsed && (
                                        <div className="wrt-records">
                                            <table className="wrt-table">
                                                <thead>
                                                    <tr>
                                                        <th>Day</th>
                                                        <th>Date</th>
                                                        <th>Shift</th>
                                                        <th>Start</th>
                                                        <th>End</th>
                                                        <th>Break</th>
                                                        <th>Hours</th>
                                                        <th>Pay</th>
                                                        <th />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {emp.records.map(rec => {
                                                        const key = `${rec.employeeID}-${rec.workday}-${rec.shift}`;
                                                        const pay = fmtPay(rec.hoursWorked, rec.rate);
                                                        return (
                                                            <tr key={key}
                                                                className={rec.dayName === 'Saturday' || rec.dayName === 'Sunday' ? 'wrt-weekend' : ''}>
                                                                <td className="wrt-day" data-label="Day">
                                                                    {DAY_SHORT[rec.dayName] || rec.dayName}
                                                                </td>
                                                                <td className="wrt-date" data-label="Date">{fmtDate(rec.workday)}</td>
                                                                <td className="wrt-shift" data-label="Shift">
                                                                    <span className="wrt-shift-badge">{rec.shiftName || `S${rec.shift}`}</span>
                                                                </td>
                                                                <td className="wrt-time" data-label="Start">{fmtTime(rec.startTime)}</td>
                                                                <td className="wrt-time" data-label="End">{fmtTime(rec.endTime)}</td>
                                                                <td className="wrt-break" data-label="Break">
                                                                    {rec.breakMinutes > 0 ? `${rec.breakMinutes}m` : '—'}
                                                                </td>
                                                                <td className="wrt-hours" data-label="Hours">
                                                                    <strong>{fmtH(rec.hoursWorked)}</strong>
                                                                </td>
                                                                <td className="wrt-pay" data-label="Pay">
                                                                    {pay ? <span className="wrt-pay-val">${pay}</span> : '—'}
                                                                </td>
                                                                <td className="wrt-actions" data-label="">
                                                                    <button className="wrt-icon-btn" title="Edit"
                                                                        onClick={() => setPopup({ open:true, editData: rec, date: null })}>
                                                                        <i className="fas fa-pen" />
                                                                    </button>
                                                                    <button className="wrt-icon-btn wrt-icon-btn--danger" title="Delete"
                                                                        disabled={deleting === key}
                                                                        onClick={() => handleDelete(rec)}>
                                                                        <i className={deleting===key ? 'fas fa-spinner fa-spin' : 'fas fa-trash'} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="wrt-tfoot">
                                                        <td colSpan={6} className="wrt-tfoot__label">Weekly total</td>
                                                        <td className="wrt-hours"><strong>{empHours.toFixed(1)}h</strong></td>
                                                        <td className="wrt-pay">
                                                            {weekPay
                                                                ? <span className="wrt-pay-val wrt-pay-val--total">${weekPay}</span>
                                                                : '—'}
                                                        </td>
                                                        <td />
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}

            <WorkReportPopup
                isOpen={popup.open}
                onClose={() => setPopup(p => ({ ...p, open: false }))}
                editData={popup.editData}
                prefillDate={popup.date}
                onSaved={load}
            />

            {rateEdit && (
                <div className="pp-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !rateEdit.saving) setRateEdit(null); }}>
                    <div className="pp-modal wrt-rate-modal">
                        <div className="pp-modal-title">Hourly rate — {rateEdit.name}</div>
                        <div className="pp-modal-row">
                            <label>New rate ($/h)<input type="number" min="0" step="0.01" autoFocus value={rateEdit.rate}
                                onChange={e => setRateEdit(r => ({ ...r, rate: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') saveRate(); }} /></label>
                            <label>Effective from<input type="date" value={rateEdit.from} onChange={e => setRateEdit(r => ({ ...r, from: e.target.value }))} /></label>
                        </div>
                        <div className="pp-muted wrt-rate-hint">Days before this date keep the previous rate; the employee card gets the new rate.</div>
                        {rateEdit.error && <div className="pp-modal-err">{rateEdit.error}</div>}
                        <div className="pp-modal-actions">
                            <button className="pp-btn" onClick={() => setRateEdit(null)} disabled={rateEdit.saving}>Cancel</button>
                            <button className="pp-btn pp-btn-primary" onClick={saveRate} disabled={rateEdit.saving}>{rateEdit.saving ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkReportTable;
